import { z } from 'zod';

import { httpClient } from '@/shared/api/httpClient';

import {
  closeOutcomeSchema,
  commitmentDetailSchema,
  commitmentSummarySchema,
  dashboardSchema,
  iterationPreviewSchema,
  iterationStartedSchema,
  teamWorkloadSchema,
  reopenOutcomeSchema,
} from '../model/types';
import type {
  CloseOutcome,
  CommitmentCloseReason,
  CommitmentDetail,
  CommitmentScope,
  CommitmentSummary,
  CreateCommitmentInput,
  Dashboard,
  IterationPreview,
  IterationStarted,
  ReopenOutcome,
  TeamWorkload,
} from '../model/types';

/**
 * Czyste funkcje endpointów — zero Reacta, zero stanu.
 *
 * Każda odpowiedź przechodzi przez `zod`, bo rozjazd kontraktu ma wybuchać w miejscu,
 * w którym powstał, a nie trzy komponenty dalej jako „cannot read property of undefined".
 * Ochrona przy budowaniu (decyzja 74) i ta w czasie działania to **dwie różne bramki**:
 * pierwsza pilnuje kształtu opisanego w dokumencie, druga tego, co naprawdę przyszło drutem.
 */

const commitmentListSchema = z.array(commitmentSummarySchema);

/**
 * Tworzenie zobowiązania — dziś wywołuje to omnibox (`!`, spec 6.6).
 *
 * Gospodarz idzie **identyfikatorem**, nie nazwą: rozwiązanie nazwy na element należy
 * do omniboksa (spec 6.1, panel wyboru przy niejednoznaczności), a nie do tej warstwy.
 */
export async function createCommitment(input: CreateCommitmentInput): Promise<CommitmentSummary> {
  const response = await httpClient.post<unknown>('/commitments', input);

  return commitmentSummarySchema.parse(response);
}

/**
 * Zobowiązania na elemencie — treść panelu E1.
 *
 * ⚠ Zakres jest parametrem serwera, nie filtrem na froncie (decyzja 63: widać zobowiązania
 * **wszystkich** userów, więc lista bywa długa). Panel woła to dwa razy — osobno po otwarte
 * i osobno po zamknięte — bo historia mieszka w zwiniętej sekcji (decyzja 66) i nie ma
 * powodu ściągać jej, dopóki nikt jej nie rozwinie.
 */
export async function fetchForElement(
  elementId: number,
  scope: CommitmentScope,
  signal?: AbortSignal,
): Promise<CommitmentSummary[]> {
  const response = await httpClient.get<unknown>(
    `/elements/${elementId}/commitments?scope=${scope}`,
    { ...(signal ? { signal } : {}) },
  );

  return commitmentListSchema.parse(response);
}

/** Pojedyncze zobowiązanie z historią i podglądem „co się stanie". */
export async function fetchCommitment(
  id: number,
  signal?: AbortSignal,
): Promise<CommitmentDetail> {
  const response = await httpClient.get<unknown>(
    `/commitments/${id}`,
    { ...(signal ? { signal } : {}) },
  );

  return commitmentDetailSchema.parse(response);
}

/** Zapowiedź odcięcia iteracji — zero zapisu (spec sek. 7). */
export async function fetchIterationPreview(
  elementId: number,
  signal?: AbortSignal,
): Promise<IterationPreview> {
  const response = await httpClient.get<unknown>(
    `/elements/${elementId}/iteration/preview`,
    { ...(signal ? { signal } : {}) },
  );

  return iterationPreviewSchema.parse(response);
}

/** Odcięcie iteracji — właściciel elementu albo admin (spec 9.2, decyzja 82 odmawia liderowi). */
export async function startIteration(elementId: number): Promise<IterationStarted> {
  const response = await httpClient.post<unknown>(`/elements/${elementId}/iteration/start`);

  return iterationStartedSchema.parse(response);
}

/**
 * Zamknięcie zobowiązania. **Powód nie jest metadanymi — jest treścią decyzji** (spec sek. 3):
 * `done` odpala zależne Surty, `obsolete` je osierocia (decyzja 21). To ta sama akcja
 * w interfejsie i dwa różne zdarzenia w świecie.
 */
export async function closeCommitment(
  id: number,
  reason: CommitmentCloseReason,
): Promise<CloseOutcome> {
  const response = await httpClient.post<unknown>(`/commitments/${id}/close`, { reason });

  return closeOutcomeSchema.parse(response);
}

/**
 * Ponowne otwarcie. ⛔ Serwer odmawia, gdy zobowiązanie obudzone tym zamknięciem
 * zostało już **zrobione** (decyzja 22: kontrakt Surta jest wykonany i nie da się go cofnąć).
 * Front tego nie sprawdza — odmowa przychodzi kodem błędu i ląduje w toaście.
 */
export async function reopenCommitment(id: number): Promise<ReopenOutcome> {
  const response = await httpClient.post<unknown>(`/commitments/${id}/reopen`, {});

  return reopenOutcomeSchema.parse(response);
}

/**
 * Pulpit — **jedno wywołanie na cały ekran**, nie sześć.
 *
 * Sześć osobnych zapytań pod sześć kafli wyglądałoby czyściej w kodzie i gorzej na ekranie:
 * kafle pojawiałyby się kolejno, a licznik przy jednym z nich mógłby pochodzić z innej
 * chwili niż przy sąsiednim. Pulpit ma pokazywać **jeden spójny stan pracy**.
 */
export async function fetchDashboard(signal?: AbortSignal): Promise<Dashboard> {
  const response = await httpClient.get<unknown>('/dashboard', {
    ...(signal ? { signal } : {}),
  });

  return dashboardSchema.parse(response);
}

/**
 * „Przyjąłem do wiadomości" — **jedyna akcja pulpitu** (decyzja 66, B3).
 *
 * Skrzynka jest jedyną listą, która **bez akcji nigdy się nie wyzeruje**: wszystko inne
 * zamyka się tam, gdzie mieszka robota. Serwer odmawia, gdy wiadomość nie jest adresowana
 * do tego użytkownika — zdejmuje ją właściciel elementu, nie ktokolwiek.
 */
export async function acknowledgeNotice(noticeId: number): Promise<void> {
  await httpClient.post<void>(`/dashboard/notices/${noticeId}/acknowledge`, {});
}

/** Obciążenie zespołów, w których jestem (decyzja 82). Zakres wyznacza serwer z członkostwa. */
export async function fetchTeamWorkload(signal?: AbortSignal): Promise<TeamWorkload[]> {
  const response = await httpClient.get<unknown>('/team-workload', {
    ...(signal ? { signal } : {}),
  });

  return z.array(teamWorkloadSchema).parse(response);
}
